import { useRef, useEffect, useState, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  className?: string;
  delay?: number;
}

export default function ScrollRevealSection({ children, className = '', delay = 0 }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0, rootMargin: '0px 0px -100px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hiddenStyle: React.CSSProperties = {
    opacity: 0,
    transform: 'translateY(32px)',
    // No willChange here — avoids 15+ simultaneous GPU compositing layers on load
  };

  const visibleStyle: React.CSSProperties = {
    opacity: 1,
    transform: 'translateY(0px)',
    transition: [
      `opacity 0.65s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      `transform 0.65s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
    ].join(', '),
  };

  return (
    <div
      ref={ref}
      className={className}
      style={visible ? visibleStyle : hiddenStyle}
    >
      {children}
    </div>
  );
}
